import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { AiFillEyeInvisible, AiFillEye } from "react-icons/ai";
import moment from "moment";
import { useDispatch } from "react-redux";
import { loginAttempt } from "store/actions/auth.actions";
import { clearAiEnablementRemindLater } from "components/AiEnablement/aiEnablementNoticeState";
import LoaderButton from "components/common/LoaderButton/LoaderButton";
import { EMAIL_REGEX_EXP, USER_ROLE } from "Constants/constant";
import { normalizeServiceProviderForClient } from "Utils/serviceProviderStatus.utils";
import { SuperAdminNavigationSections } from "Constants/Navigations/SuperAdminNavigations";
import { getDefaultPlatformAdminRoute } from "Constants/Navigations/superAdminNavigation.utils";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import DOMPurify from "dompurify";
import vertex360DarkMark from "assets/auth/vertex360-wordmark-dark.png";
import supportlyWhiteMark from "assets/auth/supportly-wordmark-white.png";
import vertex360WhiteMark from "assets/auth/vertex360-wordmark-white.png";
import "./SignIn.css";
import { setExpireTime, setToken } from "helpers/Tokens";
import { groupByPermissionsByName, parseBooleanVal, cacheCareSector, getCachedCareSector } from "Utils/utils";
import api from "store/services/auth.services";
import spApi from "store/services/sp.services";
import { spRosteringSettings, spRosteringSettingsRequest } from "store/actions/sp.actions";
import { toast } from "react-toastify";
import momentTimeZone from "moment-timezone";
import { useLocation } from 'react-router-dom';
import { URL_GET_SLUG_FROM_URL } from "store/services/URL";
import {
  mapWorkerProviderFromLogin,
  resolveDefaultWorkerProvider,
} from "helpers/workerProviderAccess.utils";


const SignIn = () => {
  const dispatch = useDispatch();
  const [passwordType, setPasswordType] = useState("password");
  const [error, setError] = useState("");
  const showPasswordHandler = () => {
    passwordType === "password"
      ? setPasswordType("text")
      : setPasswordType("password");
  };
  const [request, setRequest] = useState({
    isRequested: false,
    isSuccess: false,
    isFailed: false,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const getSlug = Array.isArray(pathSegments)  && pathSegments?.length > 0 ? pathSegments[0] : ''
  const hasSlug =Array.isArray(pathSegments) ? pathSegments?.length > 0 : false;

  // Reserved route names - branding API expects a service provider slug, not app routes
  const RESERVED_SLUGS = [
    'dashboard', 'login', 'signup', 'signin', 'forget-password', 'reset-password',
    'confirmpassword', 'select-otp', 'setup-otp', 'verify-otp', 'confirm-otp',
    'verified-otp', 'email-verification-code', 'sp-invitation', 'member-invitation',
    'settings', 'crm', 'hrm', 'participant', 'worker', 'release-notes', 'reports',
    'rostering', 'sil', 'subscription', 'cos', 'ah', 'forms-list', 'forms-list-hrm',
    // Platform Admin console (/:slug login route must not hit branding API)
    'tenants', 'users', 'staff-users', 'platform-roles', 'audit-log', 'ghost-sessions',
    'plans', 'usage', 'prospects', 'sp-list',
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [slugData,setSlugData]=useState(null)

  useEffect(()=>{
    const isServiceProviderSlug = getSlug && getSlug !== '' && !RESERVED_SLUGS.includes(getSlug.toLowerCase());
    if (isServiceProviderSlug) {
      apiGetRequest(`${URL_GET_SLUG_FROM_URL}/${getSlug}`).then((res)=>{
        setSlugData(res?.data?.data)
      }).catch((error)=>{
        console.log({error})
      })
    } else {
      setSlugData(null);
    }
  },[getSlug])

  const handleSendEmailVerifyCode = async (email) => {
    try {
      const res = await api.resendEmailVerificatioCode({ email: email });
      if (res) {
        toast.success(
          res?.message ??
            "Email verification code successfully send, Check your email"
        );
        const encode = window.btoa(encodeURIComponent(email));
        navigate(`/email-verification-code/${encode}`);
      }
    } catch (err) {
      setRequest({ ...request, isRequested: false, isFailed: true });
      throw new Error(err);
    }
  };

  const login = async (data) => {
    setError("");
    setRequest({ ...request, isRequested: true, isFailed: false });
    localStorage.clear();
    sessionStorage.clear();
    try {
      const res = await apiPostRequest("login", data, null);
      if (res.status === 200) {
        // Check email is verified
        if (
          res.data &&
          res.data.user &&
          res.data.user.hasOwnProperty("email_verified")
        ) {
          handleSendEmailVerifyCode(data.email);
          return;
        }

        // Check user permissions
        if (res.data?.user?.permissions) {
          const groupedPermissions = groupByPermissionsByName(
            res.data?.user?.permissions || []
          );
          Object.assign(res?.data?.user, {
            permissions: { ...groupedPermissions },
          });
        }
        //check already selected regions for manager 
        if (res?.data?.user?.regions) {
          const managerRegions = res?.data?.user?.regions
            ?.filter((item) => {
              //pivot object only comes in manager portal response
              if(item?.pivot){
                return item?.pivot?.status;
              }else{
                return item?.status;
              }
              })
            ?.map((el) => ({
              label: el?.name,
              value: el?.id,
            }));

          localStorage.setItem(
            "selectedRegions",
            JSON.stringify(managerRegions)
          );
        }

        // check service providers for standard user
        if (res?.data?.user?.userType === 'sp_standard_user' && res?.data?.user?.providers) {
          const workerProviders = res?.data?.user?.providers?.map(
            mapWorkerProviderFromLogin
          );
          const parsedProviders = JSON.stringify(workerProviders);
          localStorage.setItem(
              "providersList",
              parsedProviders
          );
          const defaultProvider = resolveDefaultWorkerProvider(workerProviders);
          localStorage.setItem("selectedProvider", JSON.stringify(defaultProvider));
        }else{
          localStorage.removeItem("providersList");
          localStorage.removeItem("selectedProvider");
        }

        // check payment setting object
        if (res.data?.user?.payment_setting !== null) {
          Object.assign(res.data?.user, {
            is_default_card:
              res.data.user?.payment_setting?.default_payment_method_id || null,
          });
          const payment_setting = res.data.user?.payment_setting;

          // Check Trial period Modal Need to be fire or not
          if (
            parseBooleanVal(payment_setting?.on_trial) &&
            payment_setting.subscription_id === null
          ) {
            
            const trialEndDate = moment(
              payment_setting?.trial_end_date,
              "YYYY-MM-DD"
            );
            const tomorrow = moment().add(1, "days").startOf("day");
            const isSameDay = trialEndDate.isSame(moment(), "day");
            const isTomorrow = trialEndDate.isSame(tomorrow, "day");
            if (isTomorrow || isSameDay) {
              localStorage.setItem("isTrailEndTomorrow", false);
            }
          }

          if (
            payment_setting.current_subscription_status === "past_due" &&
            payment_setting.subscription_id !== null &&
            parseBooleanVal(payment_setting.subscription_status) === false
          ) {
            localStorage.setItem("isStripeGracePeriod", false);
          }
        }

        // check authentication activity object
        if (
          res.data &&
          res.data.user &&
          res.data.user.hasOwnProperty("authentication_activity")
        ) {
          // console.log("here");
          localStorage.setItem(
            "2FARequired",
            res.data.user.authentication_activity.otp_verified
          );
          localStorage.setItem("2FASetup", res.data.user.phone_2fa);
          localStorage.setItem(
            "authentication_uuid",
            res.data.user.authentication_activity.authentication_uuid
          );
        }

        clearAiEnablementRemindLater();

        // Sp provider setting object dispatcher
        if (
          res.data &&
          "service_provider" in res.data.user &&
          res.data.user.service_provider &&
          res.data.user.userType === USER_ROLE.Sp_Admin
        ) {
          const serviceProvider = normalizeServiceProviderForClient(
            res.data.user.service_provider
          );
          localStorage.setItem("spSettings", JSON.stringify(serviceProvider));
          cacheCareSector(res.data.user);
          cacheCareSector(res.data.user.service_provider);
          dispatch(
            loginAttempt.success({
              ...res.data.user,
              service_provider: serviceProvider,
              name: serviceProvider.name,
            })
          );
        } else {
          dispatch(loginAttempt.success(res.data.user));
        }

        const logInTime = moment().format("HH:mm");
        const warnTime = moment().add(120000, "milliseconds").format("HH:mm");
        const logoutTime = moment()
          .add(1200000, "milliseconds")
          .format("HH:mm");
        setExpireTime({ logInTime, warnTime, logoutTime });
        setRequest({ ...request, isRequested: false, isSuccess: true });

        // store in Localstorage
        localStorage.setItem(
          "isOnBoarding",
          res.data.user.userType === USER_ROLE.Sp_Standard_User &&
            !res.data.user.on_boarding
            ? false
            : true
        );
        localStorage.setItem("userType", res.data.user.userType);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        cacheCareSector(res.data.user);
        setToken(res.data.user.AccessToken);

        const spPortalRoles = [
          USER_ROLE.Sp_Standard_User,
          USER_ROLE.Sp_manager,
          USER_ROLE.Sp_Admin,
        ];
        const isOtpVerified = parseBooleanVal(
          localStorage.getItem("2FARequired")
        );
        // OTP-protected routes (e.g. general-setting) must not be called before 2FA —
        // a 322 clears localStorage and dumps the user back on the login page.
        let prefetchedRosteringSettings = null;
        if (
          isOtpVerified &&
          spPortalRoles.includes(res.data.user.userType) &&
          !getCachedCareSector()
        ) {
          try {
            const settingsRes = await spApi.getSpRosteringSettingApi();
            if (settingsRes?.general_settings?.care_sector) {
              cacheCareSector(settingsRes.general_settings.care_sector);
              prefetchedRosteringSettings = settingsRes.general_settings;
            }
          } catch (prefetchError) {
            console.warn("Failed to prefetch care sector settings", prefetchError);
          }
        }

        if (!isOtpVerified) {
          localStorage.setItem("step", "0.5");
          navigate("/select-otp");
          return;
        } else if (
          parseBooleanVal(localStorage.getItem("isOnBoarding")) === false
        ) {
          localStorage.setItem("step", false);
          navigate("/");
          return;
        } else {
          localStorage.setItem("step", false);
          if (
            res.data?.user?.userType === USER_ROLE.Sp_Standard_User ||
            res.data?.user?.userType === USER_ROLE.Sp_manager ||
            res.data?.user?.userType === USER_ROLE.Sp_Admin
          ) {
            if (prefetchedRosteringSettings) {
              dispatch(
                spRosteringSettings.success(prefetchedRosteringSettings)
              );
            } else {
              dispatch(spRosteringSettingsRequest());
            }
          }
          const landingRoute =
            res.data.user.userType === USER_ROLE.Super_Admin
              ? getDefaultPlatformAdminRoute(SuperAdminNavigationSections)
              : "/dashboard";
          navigate(landingRoute);
        }
      }
    } catch (err) {
      setRequest({ ...request, isRequested: false, isFailed: true });
      setError(err.message);
      console.log("err", err);
    }
  };
  const onSubmit = (data) => {
    const currentTimeZone = momentTimeZone.tz.guess();
    login({ ...data, timezone: currentTimeZone });
  };

  return (
    <div className='signin-page'>
      <div className='signin-panel'>
        <div className='signin-box'>
          <div className='signin-mark-row'>
            <img src={vertex360DarkMark} alt='Vertex360' />
          </div>
          <h1 className='signin-title'>Sign in to your account</h1>
          <p className='signin-sub'>
            Your login stays right here while we finish moving it across to
            Supportly.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className='signin-form'>
            <div className='signin-field'>
              <label htmlFor='email'>Email address</label>
              <input
                {...register("email", {
                  setValueAs: (value) => value.trim(),
                  required: true,
                  pattern: EMAIL_REGEX_EXP,
                })}
                id='email'
                name='email'
                type='email'
                autoComplete='email'
                required
                placeholder='you@provider.org.au'
                className={`signin-input ${
                  request.isFailed || errors?.email ? "signin-input--error" : ""
                }`}
              />
              {errors?.email?.type === "required" && (
                <span className='signin-error'>This field is required!</span>
              )}
              {errors?.email?.type === "pattern" && (
                <span className='signin-error'>
                  Please enter a valid email address!
                </span>
              )}
            </div>

            <div className='signin-field'>
              <label htmlFor='password'>Password</label>
              <div className='signin-field-wrap'>
                <input
                  {...register("password", {
                    required: true,
                  })}
                  id='password'
                  name='password'
                  type={passwordType}
                  autoComplete='current-password'
                  required
                  placeholder='••••••••••'
                  className={`signin-input signin-input--password ${
                    request.isFailed || errors?.password
                      ? "signin-input--error"
                      : ""
                  }`}
                />
                <span
                  onClick={showPasswordHandler}
                  className={`signin-eye ${
                    request.isFailed ? "signin-eye--error" : ""
                  }`}
                >
                  {passwordType === "password" ? (
                    <AiFillEyeInvisible />
                  ) : (
                    <AiFillEye />
                  )}
                </span>
              </div>
              {errors?.password?.type === "required" && (
                <span className='signin-error'>This field is required!</span>
              )}
              {error && error.length > 0 && (
                <span className='signin-error'>{error}</span>
              )}
            </div>

            <div className='signin-row'>
              <label htmlFor='remember-me' className='signin-remember'>
                <input
                  {...register("remember-me", {})}
                  id='remember-me'
                  name='remember-me'
                  type='checkbox'
                />
                Remember me
              </label>
              <Link to='/forget-password' className='signin-forgot'>
                Forgot password?
              </Link>
            </div>

            {request.isRequested ? (
              <LoaderButton
                classes='signin-submit'
                svgClasses='signin-submit-spin animate-spin'
                textbutton='Logging in...'
              />
            ) : (
              <button type='submit' className='signin-submit'>
                Sign in
              </button>
            )}

            <p className='signin-footnote'>
              Need a hand?{" "}
              <a href='mailto:support@vertex360.io'>Contact support</a> — same
              team, new name.
              <br />
              Don’t have an account? <Link to='/signup'>Sign up</Link>
            </p>
          </form>
        </div>
      </div>

      <div className='signin-brand'>
        <div className='signin-eyebrow'>Grows with your Mission</div>
        <div className='signin-lockup'>
          <img
            className='signin-supportly-mark'
            src={supportlyWhiteMark}
            alt='Supportly'
          />
          <div className='signin-arrow' aria-hidden='true'>
            <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M12 19V5M12 5L6 11M12 5L18 11'
                stroke='#fff'
                strokeWidth='2.4'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <img
            className='signin-v360-mark'
            src={vertex360WhiteMark}
            alt='Vertex360'
          />
        </div>
        <div className='signin-headline'>
          Supportly <span className='signin-headline-strong'>powered by Vertex360</span>
        </div>
        {slugData?.branding_text ? (
          <div
            className='signin-branding-html'
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(slugData?.branding_text || "", {
                ALLOWED_ATTR: ["target", "href", "rel"],
              }),
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

export default SignIn;
